import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { MSym, StoreoLogo } from '@storeo/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money } from '../../lib/format';

const STOREFRONT_ROOT = import.meta.env.VITE_STOREFRONT_ROOT_DOMAIN ?? 'storeo.com.br';
const RAIL_KEY = 'painel:rail';

type NavItem = {
  label: string;
  icon: string;
  to?: string;
  end?: boolean;
  tag?: string;
  soon?: boolean;
};

/** Nav do redesign Lumina (docs/prototype/storeo_dashboard_do_lojista_centro_de_comando) —
 *  rótulos/agrupamento do protótipo mapeados nas 8 rotas reais. Itens sem rota entram como
 *  rótulos/agrupamento do protótipo, mapeados nas rotas reais. */
const NAV_GROUPS: { title: string; items: NavItem[] }[] = [
  {
    title: 'Principal',
    items: [
      { label: 'Visão Geral', icon: 'dashboard', to: '/', end: true },
      { label: 'Vendas & Pedidos', icon: 'receipt_long', to: '/vendas' },
      { label: 'Analytics & Métricas', icon: 'insights', to: '/analytics' },
    ],
  },
  {
    title: 'Gestão',
    items: [
      { label: 'Produtos', icon: 'inventory_2', to: '/produtos' },
      { label: 'Clientes & LTV', icon: 'group', to: '/clientes' },
      { label: 'Links de Venda', icon: 'link', to: '/links' },
    ],
  },
  {
    title: 'Operação & Financeiro',
    items: [
      {
        label: 'Pagamentos & Repasses',
        icon: 'account_balance_wallet',
        to: '/dinheiro',
        tag: 'PIX',
      },
      { label: 'Automações & Bots', icon: 'smart_toy', to: '/campanhas' },
    ],
  },
  {
    title: 'Sistema & Personalização',
    items: [
      { label: 'Personalizar Loja', icon: 'palette', to: '/loja' },
      { label: 'Configurações & Domínios', icon: 'tune', to: '/config' },
    ],
  },
];

const MOBILE_NAV: NavItem[] = [
  { label: 'Início', icon: 'dashboard', to: '/', end: true },
  { label: 'Pedidos', icon: 'receipt_long', to: '/vendas' },
  { label: 'Catálogo', icon: 'inventory_2', to: '/produtos' },
  { label: 'Automações', icon: 'smart_toy', to: '/campanhas' },
  { label: 'Ajustes', icon: 'tune', to: '/config' },
];

const ALL_ITEMS = NAV_GROUPS.flatMap((g) => g.items);

const linkBase =
  'flex items-center justify-between px-space-sm py-space-xs rounded-lg transition-all group';
const linkIdle = 'text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface';
const linkActive =
  'bg-primary-container text-on-primary-container font-semibold shadow-[0_0_20px_rgba(99,102,241,0.35)]';
const iconLinkBase =
  'flex items-center justify-center w-11 h-11 rounded-lg mx-auto transition-all group';

export function Shell() {
  const { claims, logout } = useAuth();
  const { pathname } = useLocation();
  const store = useQuery({ queryKey: ['store'], queryFn: () => api.store() });
  const balance = useQuery({ queryKey: ['balance'], queryFn: () => api.balance() });

  const [railOpen, setRailOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(RAIL_KEY) !== 'collapsed';
    } catch {
      return true;
    }
  });
  function toggleRail() {
    setRailOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem(RAIL_KEY, next ? 'open' : 'collapsed');
      } catch {
        /* private mode / storage off — só não persiste */
      }
      return next;
    });
  }

  const person = claims?.name ?? claims?.email ?? 'Parceiro';
  const storeName = store.data?.name ?? 'Sua loja';
  const subdomain = store.data?.subdomain;
  const host = subdomain ? `${subdomain}.${STOREFRONT_ROOT}` : STOREFRONT_ROOT;
  const crumb =
    ALL_ITEMS.find((n) => (n.end ? pathname === n.to : n.to && pathname.startsWith(n.to)))?.label ??
    'Painel';

  const asideW = railOpen ? 'w-72' : 'w-20';
  const shiftL = railOpen ? 'md:left-72' : 'md:left-20';
  const padL = railOpen ? 'md:pl-72' : 'md:pl-20';

  /** O Studio é um editor — usa toda a largura disponível (o canvas escala pra caber).
   *  As demais telas ficam contidas em 1080px como no protótipo. */
  const fullBleed = pathname.startsWith('/loja');
  const contentW = fullBleed ? 'max-w-none px-3 md:px-5' : 'max-w-[1080px] px-4 md:px-8';

  function navItem(item: NavItem) {
    if (!railOpen) {
      const content = <MSym name={item.icon} className="text-[20px]" />;
      if (item.soon || !item.to) {
        return (
          <span
            key={item.label}
            className={`${iconLinkBase} ${linkIdle} opacity-40 cursor-not-allowed`}
            title={`${item.label} · em breve`}
          >
            {content}
          </span>
        );
      }
      return (
        <NavLink
          key={item.label}
          to={item.to}
          end={item.end}
          title={item.label}
          className={({ isActive }) =>
            `${iconLinkBase} ${isActive ? linkActive : `${linkIdle} group-hover:text-primary`}`
          }
        >
          {content}
        </NavLink>
      );
    }

    if (item.soon || !item.to) {
      return (
        <span
          key={item.label}
          className={`${linkBase} ${linkIdle} opacity-50 cursor-not-allowed`}
          title="Em breve"
        >
          <span className="flex items-center gap-space-sm">
            <MSym name={item.icon} className="text-[20px]" />
            <span className="font-label-lg text-label-lg">{item.label}</span>
          </span>
          <span className="font-label-sm text-label-sm px-space-2xs py-space-3xs rounded-md bg-surface-container text-outline">
            em breve
          </span>
        </span>
      );
    }
    return (
      <NavLink
        key={item.label}
        to={item.to}
        end={item.end}
        className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
      >
        {({ isActive }) => (
          <>
            <span className="flex items-center gap-space-sm">
              <MSym
                name={item.icon}
                className={`text-[20px] transition-colors ${isActive ? '' : 'group-hover:text-primary'}`}
              />
              <span className="font-label-lg text-label-lg">{item.label}</span>
            </span>
            {item.tag ? (
              <span className="font-label-sm text-label-sm px-space-2xs py-space-3xs rounded-md bg-secondary-container text-on-secondary-container font-semibold">
                {item.tag}
              </span>
            ) : isActive ? (
              <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            ) : null}
          </>
        )}
      </NavLink>
    );
  }

  return (
    <div className="min-h-screen bg-surface-container-lowest">
      {/* rail — desktop */}
      <aside
        className={`lumina hidden md:flex fixed left-0 top-0 h-screen ${asideW} bg-surface-container-low flex-col justify-between z-50 shadow-[0_1px_8px_rgba(0,0,0,0.5)] transition-[width] duration-200`}
      >
        <div className="flex flex-col flex-1 min-h-0">
          <div className={`pt-space-md pb-space-sm ${railOpen ? 'px-space-md' : 'px-space-xs'}`}>
            <div
              className={`flex items-center mb-space-sm ${railOpen ? 'justify-between gap-space-sm' : 'flex-col gap-space-xs'}`}
            >
              <div className="flex items-center gap-space-sm min-w-0">
                <StoreoLogo variant="mark" className="h-8 w-auto shrink-0" />
                {railOpen ? (
                  <div className="flex flex-col">
                    <span className="font-headline-sm text-headline-sm font-bold text-on-surface tracking-tight">
                      Storeo
                    </span>
                    <span className="font-label-sm text-label-sm text-primary tracking-wide uppercase">
                      Painel
                    </span>
                  </div>
                ) : null}
              </div>
              <button
                type="button"
                onClick={toggleRail}
                title={railOpen ? 'Encolher menu' : 'Expandir menu'}
                aria-label={railOpen ? 'Encolher menu' : 'Expandir menu'}
                aria-pressed={!railOpen}
                className="w-8 h-8 shrink-0 rounded-lg flex items-center justify-center text-on-surface-variant hover:text-on-surface hover:bg-surface-container-high transition-all"
              >
                <MSym name={railOpen ? 'menu_open' : 'menu'} className="text-[20px]" />
              </button>
            </div>
            {railOpen ? (
              <div className="p-space-sm rounded-xl bg-surface-container">
                <div className="flex items-center justify-between mb-space-3xs">
                  <div className="flex items-center gap-space-xs min-w-0">
                    <span className="w-2 h-2 rounded-full bg-secondary shadow-[0_0_8px_rgba(78,222,163,0.8)] shrink-0" />
                    <span className="font-label-lg text-label-lg font-semibold text-on-surface truncate">
                      {storeName}
                    </span>
                  </div>
                  <MSym name="unfold_more" className="text-outline text-[18px]" />
                </div>
                <div className="flex items-center justify-between font-label-sm text-label-sm text-on-surface-variant">
                  <span className="truncate">{host}</span>
                  <span className="text-secondary font-medium shrink-0 ml-space-xs">SSL Ativo</span>
                </div>
              </div>
            ) : null}
          </div>

          <nav
            className={`flex-1 overflow-y-auto py-space-xs ${railOpen ? 'px-space-md space-y-space-md' : 'px-space-xs space-y-space-sm'}`}
          >
            {NAV_GROUPS.map((group, gi) => (
              <div key={group.title} className="space-y-space-3xs">
                {railOpen ? (
                  <div className="px-space-xs font-label-sm text-label-sm text-outline uppercase tracking-wider mb-space-2xs">
                    {group.title}
                  </div>
                ) : gi > 0 ? (
                  <div className="mx-auto my-space-2xs h-px w-6 bg-surface-container-highest" />
                ) : null}
                {group.items.map(navItem)}
              </div>
            ))}
          </nav>
        </div>

        <div
          className={`space-y-space-sm bg-surface-container-lowest ${railOpen ? 'p-space-md' : 'p-space-xs'}`}
        >
          {railOpen ? (
            <button
              onClick={logout}
              className="w-full flex items-center gap-space-sm p-space-sm rounded-xl bg-surface-container hover:bg-surface-container-high transition-colors text-left"
            >
              <span className="w-8 h-8 rounded-full bg-primary flex items-center justify-center shrink-0">
                <MSym name="person" className="text-on-primary text-[18px]" />
              </span>
              <span className="flex flex-col min-w-0 flex-1">
                <span className="font-label-lg text-label-lg font-semibold text-on-surface truncate">
                  {person}
                </span>
                <span className="font-label-sm text-label-sm text-on-surface-variant">
                  Proprietário
                </span>
              </span>
              <MSym name="logout" className="text-outline text-[18px]" />
            </button>
          ) : (
            <button
              onClick={logout}
              title={`${person} · sair`}
              aria-label="Sair"
              className="w-11 h-11 mx-auto rounded-lg flex items-center justify-center bg-surface-container hover:bg-surface-container-high transition-colors text-on-surface-variant"
            >
              <MSym name="logout" className="text-[20px]" />
            </button>
          )}
          {railOpen ? (
            <div className="flex items-center justify-between px-space-2xs font-label-sm text-label-sm text-on-surface-variant">
              <div className="flex items-center gap-space-2xs">
                <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                <span>99.98% Uptime</span>
              </div>
              <span>Zero Latência</span>
            </div>
          ) : null}
        </div>
      </aside>

      {/* topbar — desktop */}
      <header
        className={`lumina hidden md:flex fixed top-0 ${shiftL} right-0 h-16 bg-surface/80 backdrop-blur-xl z-40 px-grid-margin-desktop items-center justify-between shadow-[0_1px_8px_rgba(0,0,0,0.4)] transition-[left] duration-200`}
      >
        <div className="flex items-center gap-space-lg flex-1">
          <div className="hidden lg:flex items-center gap-space-2xs font-label-md text-label-md text-on-surface-variant">
            <span>{storeName}</span>
            <span className="text-outline">/</span>
            <span className="text-on-surface font-semibold">{crumb}</span>
          </div>
          <div className="relative max-w-md w-full">
            <MSym
              name="search"
              className="absolute left-space-sm top-1/2 -translate-y-1/2 text-outline text-[18px]"
            />
            <input
              className="w-full h-10 pl-10 pr-12 rounded-xl bg-surface-container-low text-on-surface placeholder:text-outline font-body-sm text-body-sm focus:outline-none focus:bg-surface-container transition-all"
              placeholder="Buscar pedidos, clientes, produtos..."
              type="text"
            />
            <kbd className="absolute right-space-sm top-1/2 -translate-y-1/2 font-label-sm text-label-sm px-1.5 py-0.5 rounded bg-surface-variant text-on-surface-variant">
              ⌘K
            </kbd>
          </div>
        </div>
        <div className="flex items-center gap-space-md">
          {/* Saldo — do topo das telas de operação do protótipo. "Disponível" = api.balance();
              "A Liberar" (custódia) não tem API → placeholder. */}
          <div className="hidden lg:flex items-center gap-space-md px-space-sm py-space-2xs rounded-xl bg-surface-container-low">
            <div className="flex flex-col leading-tight">
              <span className="font-label-sm text-label-sm text-outline uppercase tracking-wider">
                Saldo Disponível (PIX)
              </span>
              <span className="font-label-md text-label-md text-secondary font-bold">
                {balance.data ? money(balance.data.available) : '—'}
              </span>
            </div>
            <div className="w-px h-7 bg-surface-container-highest/60" />
            <div className="flex flex-col leading-tight">
              <span className="font-label-sm text-label-sm text-outline uppercase tracking-wider">
                A Liberar
              </span>
              <span className="font-label-md text-label-md text-on-surface-variant font-bold">
                —
              </span>
            </div>
            <MSym name="bolt" className="text-primary text-[18px]" />
          </div>
          <div className="hidden xl:flex items-center gap-space-2xs px-space-sm py-space-2xs rounded-full bg-surface-container font-label-sm text-label-sm text-on-surface-variant">
            <span className="w-2 h-2 rounded-full bg-secondary shadow-[0_0_6px_rgba(78,222,163,0.8)]" />
            <span className="text-on-surface font-medium">Loja Aberta</span>
          </div>
          <NavLink
            to="/produtos"
            className="flex items-center gap-space-xs px-space-md py-space-xs rounded-xl bg-primary text-on-primary font-label-lg text-label-lg font-bold hover:bg-primary-fixed hover:text-on-primary-fixed transition-all shadow-[0_0_20px_rgba(99,102,241,0.35)]"
          >
            <MSym name="add" className="text-[18px]" />
            <span>Criar Produto / Link</span>
          </NavLink>
          <button className="relative p-space-xs rounded-xl text-on-surface-variant hover:bg-surface-container hover:text-on-surface transition-colors">
            <MSym name="notifications" className="text-[22px]" />
            <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full bg-secondary shadow-[0_0_6px_rgba(78,222,163,0.9)]" />
          </button>
        </div>
      </header>

      {/* mobile top bar */}
      <header className="lumina md:hidden sticky top-0 z-40 h-14 bg-surface/85 backdrop-blur-xl flex items-center justify-between px-grid-margin-mobile shadow-[0_1px_8px_rgba(0,0,0,0.4)]">
        <div className="flex items-center gap-space-xs">
          <StoreoLogo variant="mark" className="h-7 w-auto" />
          <span className="font-label-lg text-label-lg font-semibold text-on-surface truncate max-w-[9rem]">
            {storeName}
          </span>
        </div>
        <button
          onClick={logout}
          className="p-space-2xs rounded-lg text-on-surface-variant hover:text-on-surface transition-colors"
          aria-label="Sair"
        >
          <MSym name="logout" className="text-[20px]" />
        </button>
      </header>

      {/* content — fora do escopo .lumina; as telas legadas mantêm ui.module.css / tokens.css */}
      <div className={`${padL} min-h-screen transition-[padding] duration-200`}>
        <main className="md:pt-16 pb-24 md:pb-10">
          <div className={`mx-auto w-full ${contentW} pt-6 md:pt-8 flex flex-col gap-[26px]`}>
            <Outlet />
          </div>
        </main>
      </div>

      {/* bottom bar — mobile */}
      <nav className="lumina md:hidden fixed bottom-0 inset-x-0 z-50 h-16 bg-surface-container-low/95 backdrop-blur-xl border-t border-surface-container-highest/40 flex items-stretch">
        {MOBILE_NAV.map((item) => (
          <NavLink
            key={item.label}
            to={item.to!}
            end={item.end}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 ${isActive ? 'text-primary' : 'text-on-surface-variant'}`
            }
          >
            <MSym name={item.icon} className="text-[22px]" />
            <span className="font-label-sm text-[10px]">{item.label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
